import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { ComponentDefaultsRolloverService } from './component-defaults-rollover.service';

@Component({
    selector: 'gtw-component-defaults-rollover',
    templateUrl: './component-defaults-rollover.component.html',
    styleUrls: ['./component-defaults-rollover.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ComponentDefaultsRolloverComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Component properties
  loading: boolean = false;
  loadParentMEDropdown: boolean = false;
  logged_in_user: string = '';

  // Dropdown data
  filteredTaxYearData: any[] = [];
  scenarioRAF: any[] = [];
  parentMEDropDown: any[] = [];

  // Selected values
  selectedTaxYear: string = '';
  selectedScenario: string = '';
  selectedParentMes: string = '';

  baseURLs: any = { 'admin': '', 'api': '', 'custom': '', 'efile': '', 'pdfEngine': '', 'bulkPdfEngine': '', 'base_url': '' };

  filterParams: any = {};

  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  private initialized: boolean = false;

  showConfirmDialog = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };
  private pendingConfirmAction: (() => void) | null = null;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private componentDefaultsRolloverService: ComponentDefaultsRolloverService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.markForCheck();
  }

  /** JsonObjectFactory / rollover services expect `{ globalParams }`. */
  private jsonGlobalContext(): any {
    return { globalParams: this.globalService };
  }

  /**
   * Initialize component when workspace context is ready
   */
  init() {
    try {
      if (this.initialized) {
        return;
      }

      const baseUrl = this.getBaseUrl();

      console.log('Component Defaults Rollover init: Checking base URL...', baseUrl);

      const hasGlobalParams = this.globalService && Object.keys(this.globalService).length > 0;

      if (!baseUrl || baseUrl.trim() === '' || !hasGlobalParams) {
        return;
      }

      console.log('Component Defaults Rollover: Initializing with base URL:', baseUrl);
      this.initialized = true;
      this.setupUserData();
      this.getTaxYearDropDown();
    } catch (e) {
      console.error('Error in Component Defaults Rollover init:', e);
      this.cdr.detectChanges();
    }
  }

  private setupUserData() {
    // Get sso_id from multiple possible sources
    this.logged_in_user = this.USER_SETTINGS?.user?.sso_id ||
                          this.globalService?.sso_id ||
                          this.filterParams?.sso_id ||
                          '';
    console.log('Component Defaults Rollover: User set up, sso_id:', this.logged_in_user);
  }

  private getBaseUrl(): string {
    // Check all possible sources for base_url
    return this.GENERAL_CONFIG?.base_url || 
           this.GENERAL_CONFIG?.baseUrl || 
           this.baseURLs?.base_url ||
           this.globalService?.base_url ||
           this.globalService?.generalConfigData?.base_url ||
           this.filterParams?.base_url ||
           '';
  }

  /**
   * Get the Tax Year Dropdown
   * Action Code: 'e83e1x'
   * Matches AngularJS: vm.getTaxYearDropDown()
   */
  getTaxYearDropDown() {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      console.warn('Base URL not available for getTaxYearDropDown');
      return;
    }

    this.selectedTaxYear = '';
    // Get current tax year from global params (workspace context)
    const currentTaxYear = parseInt(this.globalService?.tax_year) ||
                           parseInt(this.filterParams?.tax_year) ||
                           new Date().getFullYear();

    console.log('Fetching tax year dropdown, currentTaxYear:', currentTaxYear);
    console.log('=== COMPONENT DEBUG getTaxYearDropDown ===');
    console.log('this.globalService:', this.globalService);
    if (this.globalService && Object.keys(this.globalService).length > 0) {
      console.log('globalParams.sso_id:', this.globalService.sso_id);
      console.log('globalParams.jcd_key:', this.globalService.jcd_key);
      console.log('globalParams.scenario:', this.globalService.scenario);
      console.log('globalParams.screen_key:', this.globalService.screen_key);
    } else {
      console.error('ERROR: globalService (global params) is MISSING or EMPTY!');
      console.error('Cannot make API call without globalParams. Aborting.');
      return; // Don't make the API call
    }
    console.log('==========================================');

    // Use JsonObjectFactory which automatically merges globalParams
    this.componentDefaultsRolloverService.getTaxYearDropDown(baseUrl, this.jsonGlobalContext()).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response: any) => {
          console.log('Tax year dropdown response:', response);
          const data = response?.jsonObject || [];
          
          // Filter tax years like AngularJS: TAX_YEAR group only
          const taxYearData = _.filter(data, (p: any) => {
            return _.includes(['TAX_YEAR'], p.CODE_GROUP);
          });
          
          // Filter within range: currentTaxYear to (currentTaxYear - 5)
          this.filteredTaxYearData = [];
          for (const item of taxYearData) {
            const itemYear = parseInt(item.CODE_DISP_DESC);
            if (item.CODE_GROUP === 'TAX_YEAR' && 
                itemYear <= currentTaxYear && 
                itemYear >= (currentTaxYear - 5)) {
              this.filteredTaxYearData.push(item);
            }
          }
          
          // Sort by CODE_DISP_DESC descending
          this.filteredTaxYearData = _.orderBy(this.filteredTaxYearData, ['CODE_DISP_DESC'], ['desc']);
          
          console.log('Filtered tax year data:', this.filteredTaxYearData);
          this.cdr.detectChanges();
        },
        (error: any) => {
          console.error('Error getting tax year dropdown:', error);
          this.cdr.detectChanges();
        }
      );
  }

  /**
   * Get the Scenario Dropdown
   * Action Code: 'va45va'
   * Matches AngularJS: vm.getScenarioDropDown()
   */
  getScenarioDropDown() {
    if (!this.selectedTaxYear) {
      console.warn('Component Defaults Rollover: Tax year not selected for scenario dropdown');
      return;
    }

    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      console.warn('Component Defaults Rollover: Base URL not available for getScenarioDropDown');
      return;
    }

    console.log('Component Defaults Rollover: Fetching scenario dropdown for tax year:', this.selectedTaxYear);
    console.log('Component Defaults Rollover: baseUrl:', baseUrl);
    console.log('Component Defaults Rollover: globalService:', this.globalService);

    this.componentDefaultsRolloverService.getScenarioDropDown(baseUrl, this.selectedTaxYear, this.jsonGlobalContext()).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response: any) => {
          console.log('Component Defaults Rollover: Scenario dropdown response:', response);
          const scenarioDropDown = response?.jsonObject || [];
          console.log('Component Defaults Rollover: scenarioDropDown data:', scenarioDropDown);
          
          // Filter for 'Return As Filed' only (like AngularJS)
          this.scenarioRAF = _.filter(scenarioDropDown, (p: any) => {
            return _.includes(['Return As Filed'], p.LABEL);
          });

          console.log('Component Defaults Rollover: Filtered scenarioRAF:', this.scenarioRAF);
          console.log('Component Defaults Rollover: scenarioRAF.length:', this.scenarioRAF.length);

          // Set default selection
          if (this.scenarioRAF && this.scenarioRAF.length > 0) {
            this.selectedScenario = String(this.scenarioRAF[0].VALUE);
            console.log('Component Defaults Rollover: selectedScenario set to:', this.selectedScenario);
            console.log('Component Defaults Rollover: selectedScenario type:', typeof this.selectedScenario);
            
            // Trigger change detection to show scenario dropdown
            this.cdr.detectChanges();
            
            // Auto-load parent ME dropdown after scenario is loaded
            console.log('Component Defaults Rollover: Calling getParentMeDropdown...');
            this.getParentMeDropdown();
          } else {
            console.warn('Component Defaults Rollover: No scenarios found or scenarioRAF is empty');
            this.selectedScenario = '';
            this.cdr.detectChanges();
          }
        },
        (error: any) => {
          console.error('Component Defaults Rollover: Error getting scenario dropdown:', error);
          this.scenarioRAF = [];
          this.selectedScenario = '';
          this.cdr.detectChanges();
        }
      );
  }

  /**
   * Called when tax year changes
   */
  onTaxYearChange() {
    console.log('Component Defaults Rollover: onTaxYearChange called, selectedTaxYear:', this.selectedTaxYear);
    // Reset dependent dropdowns
    this.scenarioRAF = [];
    this.selectedScenario = '';
    this.loadParentMEDropdown = false;
    this.parentMEDropDown = [];
    this.selectedParentMes = '';
    // Trigger change detection before loading new data
    this.cdr.detectChanges();
    // Load scenario dropdown
    this.getScenarioDropDown();
  }

  /**
   * Get the Parent ME Dropdown
   * Action Code: '07roqs'
   * Matches AngularJS: vm.getParentMeDropdown()
   */
  getParentMeDropdown() {
    if (!this.selectedTaxYear) {
      console.warn('Component Defaults Rollover: Tax year not selected for parent ME dropdown');
      return;
    }

    if (!this.selectedScenario) {
      console.warn('Component Defaults Rollover: Scenario not selected for parent ME dropdown');
      return;
    }

    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      console.warn('Component Defaults Rollover: Base URL not available for getParentMeDropdown');
      return;
    }

    console.log('Component Defaults Rollover: Starting to fetch Parent ME dropdown...');
    console.log('Component Defaults Rollover: - Tax Year:', this.selectedTaxYear);
    console.log('Component Defaults Rollover: - Scenario:', this.selectedScenario);
    console.log('Component Defaults Rollover: - SSO ID:', this.logged_in_user);
    console.log('Component Defaults Rollover: - Base URL:', baseUrl);

    // Set loading state and show the dropdown container
    this.loadParentMEDropdown = true;
    this.parentMEDropDown = [];
    this.selectedParentMes = '';
    this.loading = true;
    
    // Trigger change detection to show loading state
    this.cdr.detectChanges();

    this.componentDefaultsRolloverService.getParentMeDropdown(
      baseUrl, 
      this.logged_in_user, 
      this.selectedTaxYear, 
      this.selectedScenario,
      this.jsonGlobalContext()
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        console.log('Component Defaults Rollover: Parent ME dropdown response:', response);
        const data = response?.jsonObject || [];
        console.log('Component Defaults Rollover: Parent ME raw data:', data);
        
        // Filter by LEVEL_GROUP === 'Parent ME' like AngularJS
        this.parentMEDropDown = _.filter(data, (p: any) => {
          return p.LEVEL_GROUP === 'Parent ME';
        });
        
        this.loading = false;
        console.log('Component Defaults Rollover: Parent ME loaded, count:', this.parentMEDropDown.length);
        console.log('Component Defaults Rollover: Parent ME data:', this.parentMEDropDown);
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Component Defaults Rollover: Error getting parent ME dropdown:', error);
        this.parentMEDropDown = [];
        this.loading = false;
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Run the Component Defaults Rollover Process (with confirmation)
   */
  confirmRunComponentDefaultsRollover() {
    this.confirmAction(
      'Run Component Defaults Rollover',
      'Are you sure to Run the Component Defaults Rollover process?',
      () => this.runRolloverProcess(),
    );
  }

  private confirmAction(title: string, message: string, action: () => void): void {
    this.confirmModalConfig = { ...this.confirmModalConfig, title, message };
    this.pendingConfirmAction = action;
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirmYes(): void {
    this.showConfirmDialog = false;
    if (this.pendingConfirmAction) {
      this.pendingConfirmAction();
      this.pendingConfirmAction = null;
    }
    this.cdr.detectChanges();
  }

  onConfirmNo(): void {
    this.showConfirmDialog = false;
    this.pendingConfirmAction = null;
    this.cdr.detectChanges();
  }

  runRolloverProcess() {
    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar('Please select the Tax Year to run the Apportionment.', 'gtw-snackbar--red');
      return;
    }

    if (!this.selectedParentMes) {
      this.gtwSnackbarService.openSnackBar('Please select the ME(s) to run the rollover process.', 'gtw-snackbar--red');
      return;
    }

    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      this.gtwSnackbarService.openSnackBar('Base URL not configured. Please contact support.', 'gtw-snackbar--red');
      return;
    }

    const jcdKey = this.filterParams?.jcd_key || this.globalService?.jcd_key;

    this.componentDefaultsRolloverService.executeComponentDefaultsRollover(
      baseUrl,
      this.logged_in_user,
      this.selectedTaxYear,
      this.selectedScenario,
      this.selectedParentMes,
      jcdKey,
      this.jsonGlobalContext()
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        const response = result.data || result;
        const errorMessage = response.errorMessage || result.errorMessage;

        if (errorMessage && errorMessage !== 'null' && errorMessage !== '') {
          this.gtwSnackbarService.openSnackBar(errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('For the selection made, the process to run the rollover is triggered.', 'gtw-snackbar--green');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error executing component defaults rollover:', error);
        this.gtwSnackbarService.openSnackBar('Error occurred while running rollover. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Reset the selected values
   * Matches AngularJS: vm.reset()
   */
  reset() {
    this.selectedTaxYear = '';
    this.loadParentMEDropdown = false;
    this.parentMEDropDown = [];
    this.selectedParentMes = '';
    this.scenarioRAF = [];
    this.selectedScenario = '';
    this.cdr.detectChanges();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
